/**
 * UploadIndicator — floating pill in the bottom-right corner.
 *
 * Shows the number of active/queued uploads as a collapsed pill.
 * Click expands to a panel listing all jobs with progress bars, ETAs,
 * and cancel/clear controls. Rendered via React portal to document.body
 * so it stays visible regardless of which page is active.
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useUpload, formatEta, type UploadJob, type UploadPhase } from '../context/UploadContext';
import { C, glassSurface } from '@kantera/ui';
import { bottomRightClearOfHelp } from '../utils/helpButtonCorner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function phaseLabel(phase: UploadPhase, progress: number, statusUnknownLabel: string): string {
  switch (phase) {
    case 'queued':
      return 'Queued';
    case 'uploading':
      return `${Math.round(progress)}% uploading`;
    case 'processing':
      return 'Processing…';
    case 'ready':
      return 'Ready';
    case 'error':
      return 'Failed';
    case 'status_unknown':
      return statusUnknownLabel;
  }
}

function phaseColor(phase: UploadPhase): string {
  switch (phase) {
    case 'queued':
      return C.text.muted;
    case 'uploading':
      return C.accent.indigo;
    case 'processing':
      return C.accent.violet;
    case 'ready':
      return C.success;
    case 'error':
      return C.danger;
    case 'status_unknown':
      return C.warning;
  }
}

function barColor(phase: UploadPhase): string {
  switch (phase) {
    case 'error':
      return C.danger;
    case 'ready':
      return C.success;
    default:
      return C.accent.indigo;
  }
}

function effectiveProgress(job: UploadJob): number {
  if (job.phase === 'processing' || job.phase === 'ready') return 100;
  if (job.phase === 'queued') return 0;
  return job.progress;
}

// ---------------------------------------------------------------------------
// JobRow — a single upload entry in the expanded panel
// ---------------------------------------------------------------------------

interface JobRowProps {
  job: UploadJob;
  onCancel: (id: string) => void;
}

const JobRow: React.FC<JobRowProps> = ({ job, onCancel }) => {
  const { t } = useTranslation();
  const active = job.phase === 'uploading' || job.phase === 'processing';
  const pct = effectiveProgress(job);
  const eta = formatEta(job.estimatedSecondsRemaining, job.startedAt);
  const color = phaseColor(job.phase);
  const bar = barColor(job.phase);

  return (
    <div
      style={{
        padding: '10px 14px',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      {/* Filename + status row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px',
        }}
      >
        {/* Status dot */}
        <div
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
          }}
          aria-hidden="true"
        />

        {/* Name + status */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: '12px',
              fontWeight: 600,
              color: C.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={job.file.name}
          >
            {job.file.name}
          </p>
          <p
            style={{
              margin: '1px 0 0',
              fontSize: '11px',
              color: color,
              fontWeight: job.phase === 'ready' ? 700 : 400,
            }}
          >
            {phaseLabel(job.phase, job.progress, t('webUploadIndicator.statusUnknown'))}
            {job.phase === 'uploading' && eta ? ` · ${eta}` : ''}
            {job.phase === 'error' && job.errorMessage ? ` · ${job.errorMessage}` : ''}
          </p>
        </div>

        {/* File size */}
        <span
          style={{
            fontSize: '11px',
            color: C.text.muted,
            flexShrink: 0,
          }}
        >
          {formatFileSize(job.totalBytes)}
        </span>

        {/* Cancel / checkmark */}
        {active && (
          <button
            onClick={() => onCancel(job.id)}
            style={{
              flexShrink: 0,
              padding: '2px 8px',
              background: 'none',
              border: `1px solid ${C.border}`,
              borderRadius: C.radius.xs,
              color: C.text.muted,
              fontSize: '11px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'border-color 150ms ease, color 150ms ease',
            }}
            aria-label={`Cancel upload of ${job.file.name}`}
          >
            Cancel
          </button>
        )}
        {job.phase === 'ready' && (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-label="Upload complete"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M20 6L9 17l-5-5"
              stroke={C.success}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {/* Progress bar */}
      {(job.phase === 'uploading' ||
        job.phase === 'processing' ||
        job.phase === 'ready' ||
        job.phase === 'queued') && (
        <div
          style={{
            height: '3px',
            borderRadius: '2px',
            backgroundColor: C.bg.card,
            overflow: 'hidden',
          }}
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Upload progress for ${job.file.name}`}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background:
                job.phase === 'uploading'
                  ? `linear-gradient(90deg, ${C.accent.indigo}, ${C.accent.violet})`
                  : bar,
              borderRadius: '2px',
              transition: 'width 400ms ease, background 300ms ease',
            }}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// UploadIndicator — the floating pill + expanded panel
// ---------------------------------------------------------------------------

export const UploadIndicator: React.FC = () => {
  const { jobs, cancelUpload, clearCompleted, activeCount } = useUpload();
  const [expanded, setExpanded] = useState(false);

  // Nothing to show
  if (jobs.length === 0) return null;

  const hasCompleted = jobs.some(
    (j) => j.phase === 'ready' || j.phase === 'error' || j.phase === 'status_unknown',
  );

  // A failed job is NOT complete. activeCount counts only queued/uploading/processing
  // (UploadContext), so folding errors into the idle branch made the pill say
  // "1 upload complete" on an upload that failed — a coach persona reported driving home
  // believing the film was up. Failures are named first, because that is the state someone
  // needs to act on.
  const failedCount = jobs.filter((j) => j.phase === 'error').length;
  const pillLabel =
    activeCount > 0
      ? `${activeCount} upload${activeCount === 1 ? '' : 's'} in progress`
      : failedCount > 0
        ? `${failedCount} upload${failedCount === 1 ? '' : 's'} failed`
        : `${jobs.length} upload${jobs.length === 1 ? '' : 's'} complete`;

  const pillBg =
    activeCount > 0
      ? `linear-gradient(135deg, ${C.accent.indigo}, ${C.accent.violet})`
      : C.bg.elevated;

  const pillBorder = activeCount > 0 ? 'none' : `1px solid ${C.border}`;

  return ReactDOM.createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-label="Upload status"
      style={{
        // Clear of the help button — see utils/helpButtonCorner.
        ...bottomRightClearOfHelp(),
        zIndex: 1060,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '8px',
      }}
    >
      {/* Expanded panel */}
      {expanded && (
        <div
          style={{
            ...glassSurface(),
            width: '320px',
            maxHeight: '400px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: C.shadow.lg,
            animation: 'kSlideUp 200ms ease both',
          }}
        >
          {/* Panel header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: C.text.muted,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.08em',
              }}
            >
              Uploads
            </span>
            {hasCompleted && (
              <button
                onClick={clearCompleted}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.text.muted,
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: '2px 6px',
                  borderRadius: C.radius.xs,
                  transition: 'color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.color = C.text.secondary;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.color = C.text.muted;
                }}
              >
                Clear completed
              </button>
            )}
          </div>

          {/* Job list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {jobs.map((job) => (
              <JobRow key={job.id} job={job} onCancel={cancelUpload} />
            ))}
          </div>
        </div>
      )}

      {/* Collapsed pill */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          background: pillBg,
          border: pillBorder,
          borderRadius: C.radius.full,
          color: activeCount > 0 ? '#fff' : C.text.secondary,
          fontSize: '12px',
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          boxShadow: activeCount > 0 ? C.shadow.glow : C.shadow.sm,
          transition: 'box-shadow 200ms ease',
          animation: 'kSlideLeft 250ms ease both',
        }}
        aria-expanded={expanded}
        aria-label={pillLabel}
      >
        {/* Animated dot for active uploads */}
        {activeCount > 0 && (
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.8)',
              animation: 'kPulseGlow 1.2s ease-in-out infinite',
              flexShrink: 0,
            }}
            aria-hidden="true"
          />
        )}

        {/* Upload icon for idle/complete state */}
        {activeCount === 0 && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M20 6L9 17l-5-5"
              stroke={C.success}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}

        {pillLabel}

        {/* Chevron */}
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
            flexShrink: 0,
          }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>,
    document.body,
  );
};
